import { getSupportedRecorderMimeType } from '../browser-audio.util'
import { buildRecordingStreamFromState, connectTrackToActiveMixer, updateRecordingVideoComposition } from '../build-recording-stream.util'
import type { RecordingVideoCompositor } from '../compose-recording-video.util'
import type { InterviewStreamPlugin, InterviewStreamPluginContext, RecordingInitData, RecordingInitOptions } from '../interview-stream-host.type'
import {
    CHUNK_PUT_TIMEOUT_MS,
    DEFAULT_CHUNK_DURATION_MS,
    FLUSH_UPLOAD_WAIT_MS,
    NON_RETRYABLE_CHUNK_UPLOAD_STATUSES,
    RECORDER_AUDIO_BITS_PER_SECOND,
    RECORDER_VIDEO_BITS_PER_SECOND,
    RECORDER_VIDEO_HEIGHT,
    RECORDER_VIDEO_WIDTH,
    RECORDING_FRAME_RATE,
} from '../recording.constant'
import { waitForMediaRecorderStop } from '../recorder-flush.util'

/** Delays between attempts of each chunk upload step: a flaky moment on the network shouldn't cost 30s of video. */
export const CHUNK_UPLOAD_RETRY_DELAYS_MS = [500, 1500]

/** Run `attempt` until it succeeds, waiting `CHUNK_UPLOAD_RETRY_DELAYS_MS` between tries; the last error is rethrown. */
export async function withUploadRetries<T>(attempt: () => Promise<T>, onRetry?: (error: unknown, nextAttempt: number) => void): Promise<T> {
    for (let tryIndex = 0; ; tryIndex += 1) {
        try {
            return await attempt()
        } catch (error) {
            const delay = CHUNK_UPLOAD_RETRY_DELAYS_MS[tryIndex]
            if (delay === undefined || isNonRetryableUploadError(error)) throw error
            onRetry?.(error, tryIndex + 2)
            await new Promise((resolve) => setTimeout(resolve, delay))
        }
    }
}

/**
 * Runs tasks one at a time, in the order they were added. Each task's own result (or error) goes to its caller; a
 * failed task doesn't stop the ones after it.
 */
export function createSerialQueue() {
    let tail: Promise<unknown> = Promise.resolve()
    return <T>(task: () => Promise<T>): Promise<T> => {
        const run = tail.then(task, task)
        tail = run.catch(() => undefined)
        return run
    }
}

function toBlockId(index: number): string {
    const raw = `chunk-${String(index).padStart(8, '0')}`
    return btoa(raw)
}

/** Fired once when MediaRecorder successfully starts — used to align transcripts to video t=0. */
export type RecordingStartedInfo = {
    recordingStartedAt: string
    clientNow: string
    recordingOriginMs: number
}

/**
 * Recorder problems and the flush summary, for the interview's session log. Without these, a dropped chunk or a
 * recorder that stopped early leaves no trace outside the candidate's browser console.
 */
export type RecordingEvent =
    | { kind: 'recorder_error'; name: string; message: string; state: RecordingState }
    | { kind: 'recorder_stopped_unexpectedly'; producedChunks: number; recordedDurationMs: number | null }
    | { kind: 'chunk_upload_failed'; chunkIndex: number; step: ChunkUploadStep; attempt: number; status: number | null; error: string; final: boolean }
    | { kind: 'recording_flush'; producedChunks: number; uploadedChunks: number; pendingAtTimeout: number; recordedDurationMs: number | null }

type ChunkUploadStep = 'part URL' | 'upload' | 'metadata'

/** A failed step of a chunk upload; `status` is the HTTP status, or null for a network error. */
class ChunkUploadError extends Error {
    constructor(
        message: string,
        readonly status: number | null
    ) {
        super(message)
    }
}

function isNonRetryableUploadError(error: unknown): boolean {
    return error instanceof ChunkUploadError && error.status !== null && NON_RETRYABLE_CHUNK_UPLOAD_STATUSES.has(error.status)
}

export type RecordingPluginHandle = {
    plugin: InterviewStreamPlugin
    initializeRecording: (options?: RecordingInitOptions) => Promise<RecordingInitData>
    setInitData: (data: RecordingInitData) => void
    requestStart: (ctx: InterviewStreamPluginContext) => Promise<void>
    scheduleFallback: (delayMs: number, callback: () => void) => void
    cancelFallback: () => void
    setOnRecordingStarted: (callback: ((info: RecordingStartedInfo) => void) | null) => void
    setOnRecordingEvent: (callback: ((event: RecordingEvent) => void) | null) => void
    getRecordingStartedInfo: () => RecordingStartedInfo | null
    /** Media-timeline offset in seconds since MediaRecorder.start(), or null if not recording. */
    getMediaOffsetSeconds: () => number | null
    /** Mute the local mic inside the recording mix (independent of track.enabled on the live track). */
    setLocalMuted: (muted: boolean) => void
    /** Stop MediaRecorder and wait for the last chunk upload; does not compose. */
    flushAndStop: () => Promise<void>
    stopAndFinalize: () => Promise<void>
}

type RecordingQualitySettings = { videoBitsPerSecond: number; audioBitsPerSecond: number; width: number; height: number; fps: number }

/** The quality recording/init picked (INT-1059), or the long-standing 1280×720 @ 30 fps, 800 / 128 kbps. */
function recordingQualityFrom(initData: RecordingInitData): RecordingQualitySettings {
    return {
        videoBitsPerSecond: initData.videoBitsPerSecond || RECORDER_VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: initData.audioBitsPerSecond || RECORDER_AUDIO_BITS_PER_SECOND,
        width: initData.videoWidth || RECORDER_VIDEO_WIDTH,
        height: initData.videoHeight || RECORDER_VIDEO_HEIGHT,
        fps: initData.videoFps || RECORDING_FRAME_RATE,
    }
}

export function createRecordingPlugin(): RecordingPluginHandle {
    let initData: RecordingInitData | null = null
    let started = false
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null
    let mediaRecorder: MediaRecorder | null = null
    let composedStream: MediaStream | null = null
    let videoCompositor: RecordingVideoCompositor | null = null
    let audioContext: AudioContext | null = null
    let merger: ChannelMergerNode | null = null
    let localMicGate: GainNode | null = null
    let localMicMuted = false
    let mixTracks: MediaStreamTrack[] = []
    const connectedTrackIds = new Set<string>()
    let uploadSessionId: string | null = null
    let writeBlobUrl: string | null = null
    let uploadProtocol: 'azure-blocks' | 'gcs-compose' = 'azure-blocks'
    let chunkIndex = 0
    const blockMap = new Map<number, string>()
    const pendingUploads: Promise<void>[] = []
    // INT-1061: one chunk upload at a time. When the uplink slows, a chunk's upload can outlast the timeslice; running
    // the next one alongside it would take even more of the uplink the live interview audio needs (INT-1043).
    const enqueueUpload = createSerialQueue()
    let ctxRef: InterviewStreamPluginContext | null = null
    let flushPromise: Promise<void> | null = null
    let recordingStartedInfo: RecordingStartedInfo | null = null
    let onRecordingStarted: ((info: RecordingStartedInfo) => void) | null = null
    let onRecordingEvent: ((event: RecordingEvent) => void) | null = null
    // Set once we ask the recorder to stop, so its stop event isn't reported as unexpected.
    let stopRequested = false
    let recordedDurationMs: number | null = null

    const reportEvent = (event: RecordingEvent) => {
        try {
            onRecordingEvent?.(event)
        } catch {
            // Reporting must never break the recording.
        }
    }

    const getMediaOffsetMs = () => (recordingStartedInfo ? Math.max(0, Math.round(performance.now() - recordingStartedInfo.recordingOriginMs)) : null)

    const uploadChunk = async (chunk: Blob, index: number, log: (message: string) => void) => {
        /** Runs one upload step with retries, reporting every failed attempt to the session log (the last one as final). */
        const runStep = async <T>(step: ChunkUploadStep, run: () => Promise<T>): Promise<T> => {
            const reportFailure = (error: unknown, attempt: number, final: boolean) => {
                const message = error instanceof Error ? error.message : String(error)
                if (!final) log(`Chunk ${index} ${step} failed (${message}); attempt ${attempt + 1}`)
                const status = error instanceof ChunkUploadError ? error.status : null
                reportEvent({ kind: 'chunk_upload_failed', chunkIndex: index, step, attempt, status, error: message, final })
            }
            let attempt = 1
            try {
                return await withUploadRetries(run, (error, nextAttempt) => {
                    reportFailure(error, nextAttempt - 1, false)
                    attempt = nextAttempt
                })
            } catch (error) {
                reportFailure(error, attempt, true)
                throw error
            }
        }
        if (!uploadSessionId || !chunk.size) {
            return
        }

        const clientToken = sessionStorage.getItem('clientToken') || ''
        let partId: string
        let putUrl: string
        let putHeaders: Record<string, string>

        if (uploadProtocol === 'gcs-compose') {
            const partData = await runStep('part URL', async () => {
                const partRes = await fetch('/api/interviews/screening-webrtc/recording/part-url', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Client-Token': clientToken,
                    },
                    body: JSON.stringify({ uploadSessionId, chunkIndex: index }),
                })
                const partJson = await partRes.json().catch(() => ({}))
                const data = partJson?.data || partJson
                if (!partRes.ok || !data?.uploadUrl || !data?.partId) {
                    throw new ChunkUploadError(partJson?.error || `Failed to mint part upload URL for chunk ${index}`, partRes.ok ? null : partRes.status)
                }
                return data as { uploadUrl: string; partId: string }
            })
            putUrl = partData.uploadUrl
            partId = partData.partId
            // Must match the Content-Type bound into the GCS V4 signed URL
            putHeaders = {
                'Content-Type': 'application/octet-stream',
            }
        } else {
            if (!writeBlobUrl) {
                return
            }
            partId = toBlockId(index)
            const separator = writeBlobUrl.includes('?') ? '&' : '?'
            putUrl = `${writeBlobUrl}${separator}comp=block&blockid=${encodeURIComponent(partId)}`
            putHeaders = {
                'x-ms-blob-type': 'BlockBlob',
                'Content-Type': chunk.type || 'application/octet-stream',
            }
        }

        const data = await chunk.arrayBuffer()
        await runStep('upload', async () => {
            const abort = new AbortController()
            const timer = setTimeout(() => abort.abort(), CHUNK_PUT_TIMEOUT_MS)
            let putRes: Response
            try {
                putRes = await fetch(putUrl, {
                    method: 'PUT',
                    headers: putHeaders,
                    body: data,
                    signal: abort.signal,
                })
            } catch (error) {
                // A network error or our timeout: both are worth another attempt (status null = retryable).
                const reason = abort.signal.aborted ? `timed out after ${CHUNK_PUT_TIMEOUT_MS / 1000}s` : error instanceof Error ? error.message : String(error)
                throw new ChunkUploadError(`Upload of recording chunk ${index} ${abort.signal.aborted ? reason : `failed: ${reason}`}`, null)
            } finally {
                clearTimeout(timer)
            }
            if (!putRes.ok) {
                throw new ChunkUploadError(`Failed to upload recording chunk ${index} (${putRes.status})`, putRes.status)
            }
        })

        blockMap.set(index, partId)
        // The server's block map is what compose trusts when the tab is gone, so this write matters as much as the PUT.
        await runStep('metadata', async () => {
            const chunkRes = await fetch('/api/interviews/screening-webrtc/recording/chunk', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Token': clientToken,
                },
                body: JSON.stringify({
                    uploadSessionId,
                    chunkIndex: index,
                    blockId: partId,
                }),
            })
            // 4xx won't get better on retry (session mismatch, recording closed).
            if (chunkRes.status >= 500) {
                throw new ChunkUploadError(`Failed to save chunk ${index} metadata (${chunkRes.status})`, chunkRes.status)
            }
        })
    }

    const finalizeRecording = async () => {
        const clientToken = sessionStorage.getItem('clientToken')
        if (!clientToken || !uploadSessionId) {
            return
        }
        const orderedBlockIds = [...blockMap.entries()].sort((a, b) => a[0] - b[0]).map(([, id]) => id)
        const finalizeRes = await fetch('/api/interviews/screening-webrtc/recording/finalize', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Client-Token': clientToken,
            },
            body: JSON.stringify({
                uploadSessionId,
                blockIds: orderedBlockIds,
                blocks: Object.fromEntries(blockMap),
                // Checked against the composed parts to flag a recording with missing footage.
                producedChunks: chunkIndex,
                uploadedChunks: blockMap.size,
                recordedDurationMs,
            }),
        })
        if (!finalizeRes.ok) {
            const json = await finalizeRes.json().catch(() => ({}))
            throw new Error(json?.error || 'Failed to finalize recording')
        }
        uploadSessionId = null
        writeBlobUrl = null
    }

    /** Tear down the compositor (its draw loop), mixer clones and AudioContext built for this recording. */
    const releaseMediaResources = () => {
        videoCompositor?.stop()
        videoCompositor = null
        mixTracks.forEach((track) => track.stop())
        mixTracks = []
        connectedTrackIds.clear()
        composedStream = null
        merger = null
        localMicGate = null
        if (audioContext) {
            audioContext.close().catch(() => undefined)
            audioContext = null
        }
    }

    const cleanupResources = () => {
        releaseMediaResources()
        started = false
        initData = null
        flushPromise = null
        recordingStartedInfo = null
        if (fallbackTimer) {
            clearTimeout(fallbackTimer)
            fallbackTimer = null
        }
    }

    const startRecorder = (stream: MediaStream, chunkDurationMs: number, quality: RecordingQualitySettings, log: (message: string) => void) => {
        const { videoBitsPerSecond, audioBitsPerSecond, width, height, fps } = quality
        const mimeType = getSupportedRecorderMimeType()
        const mixedAudio = stream.getAudioTracks()[0]
        const channelCount = mixedAudio?.getSettings?.()?.channelCount
        log(
            `Recording: MediaRecorder mime=${mimeType || 'browser-default'} audioChannels=${channelCount ?? 'unknown'} video=${width}x${height}@${fps}fps videoKbps=${Math.round(videoBitsPerSecond / 1000)} audioKbps=${Math.round(audioBitsPerSecond / 1000)}`
        )
        const recorder = new MediaRecorder(stream, {
            ...(mimeType ? { mimeType } : {}),
            audioBitsPerSecond,
            videoBitsPerSecond,
        })
        mediaRecorder = recorder
        recorder.ondataavailable = (event) => {
            if (!event.data || event.data.size === 0) return
            const index = chunkIndex++
            const chunk = event.data
            const uploadTask = enqueueUpload(() => uploadChunk(chunk, index, log))
                .catch((error: unknown) => {
                    const message = error instanceof Error ? error.message : String(error)
                    // runStep already reported it to the session log.
                    log(`Chunk upload failed: ${message}`)
                })
                .finally(() => {
                    const idx = pendingUploads.indexOf(uploadTask)
                    if (idx >= 0) {
                        pendingUploads.splice(idx, 1)
                    }
                })
            pendingUploads.push(uploadTask)
        }
        recorder.onerror = (event: Event) => {
            const error = (event as Event & { error?: DOMException }).error
            log(`Recording: MediaRecorder error ${error?.name ?? 'unknown'}: ${error?.message ?? ''} (state=${recorder.state})`)
            reportEvent({ kind: 'recorder_error', name: error?.name ?? 'unknown', message: error?.message ?? '', state: recorder.state })
        }
        recorder.addEventListener('stop', () => {
            if (stopRequested || mediaRecorder !== recorder) {
                return
            }
            log('Recording: MediaRecorder stopped unexpectedly')
            reportEvent({ kind: 'recorder_stopped_unexpectedly', producedChunks: chunkIndex, recordedDurationMs: getMediaOffsetMs() })
        })
        recorder.start(chunkDurationMs)
        const now = new Date()
        const info: RecordingStartedInfo = {
            recordingStartedAt: now.toISOString(),
            clientNow: now.toISOString(),
            recordingOriginMs: performance.now(),
        }
        recordingStartedInfo = info
        log(`Recording: MediaRecorder started at ${info.recordingStartedAt}`)
        onRecordingStarted?.(info)
    }

    const flushAndStop = async () => {
        if (flushPromise) {
            return flushPromise
        }

        flushPromise = (async () => {
            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
                fallbackTimer = null
            }

            stopRequested = true
            const recorder = mediaRecorder
            if (recorder && recorder.state !== 'inactive') {
                recordedDurationMs = getMediaOffsetMs()
                await waitForMediaRecorderStop(recorder)
            }
            mediaRecorder = null

            let waitTimer: ReturnType<typeof setTimeout> | null = null
            const timedOut = await Promise.race([
                Promise.allSettled([...pendingUploads]).then(() => false),
                new Promise<boolean>((resolve) => {
                    waitTimer = setTimeout(() => resolve(true), FLUSH_UPLOAD_WAIT_MS)
                }),
            ])
            if (waitTimer) {
                clearTimeout(waitTimer)
            }
            const pendingAtTimeout = timedOut ? pendingUploads.length : 0
            if (timedOut) {
                ctxRef?.log(`Recording: flush stopped waiting with ${pendingAtTimeout} chunk upload(s) still pending`)
            }

            // Emitted here, not in stopAndFinalize: the session log is finalized between the two.
            if (recordingStartedInfo) {
                reportEvent({ kind: 'recording_flush', producedChunks: chunkIndex, uploadedChunks: blockMap.size, pendingAtTimeout, recordedDurationMs })
            }
        })()

        return flushPromise
    }

    const hotPlugRemoteAudio = (ctx: InterviewStreamPluginContext) => {
        if (!started || !audioContext || !merger) {
            return
        }
        const remoteTrack = ctx.streams.remoteAudioTrack
        if (!remoteTrack) {
            return
        }
        const before = connectedTrackIds.size
        connectTrackToActiveMixer(audioContext, merger, remoteTrack, mixTracks, connectedTrackIds)
        if (connectedTrackIds.size > before) {
            ctx.log('Recording: remote bot audio connected to mixer (both channels)')
        }
    }

    // Mic device switches replace streams.audioTrack mid-recording — the mixer
    // still reads the old (now-disposed) pipeline's clone, so reconnect here or
    // the candidate's channel goes silent for the rest of the session.
    const hotPlugLocalAudio = (ctx: InterviewStreamPluginContext) => {
        if (!started || !audioContext || !merger) {
            return
        }
        const micTrack = ctx.streams.audioTrack
        if (!micTrack) {
            return
        }
        const before = connectedTrackIds.size
        connectTrackToActiveMixer(audioContext, merger, micTrack, mixTracks, connectedTrackIds, localMicGate)
        if (connectedTrackIds.size > before) {
            ctx.log('Recording: local mic track reconnected to mixer')
        }
    }

    const applyInitData = (data: RecordingInitData) => {
        initData = data
        uploadSessionId = data.uploadSessionId
        writeBlobUrl = data.blobWriteUrl || null
        uploadProtocol = data.uploadProtocol || (data.blobWriteUrl ? 'azure-blocks' : 'gcs-compose')
        chunkIndex = 0
        blockMap.clear()
        stopRequested = false
        recordedDurationMs = null
    }

    const plugin: InterviewStreamPlugin = {
        id: 'recording',
        start(ctx) {
            ctxRef = ctx
        },
        stop() {
            ctxRef = null
        },
        onStreamsUpdated(ctx) {
            ctxRef = ctx
            if (!started || !composedStream) {
                return
            }
            if (updateRecordingVideoComposition(videoCompositor, ctx.streams)) {
                ctx.log('Recording: video sources updated')
            }
            hotPlugLocalAudio(ctx)
            hotPlugRemoteAudio(ctx)
        },
    }

    return {
        plugin,
        async initializeRecording(options: RecordingInitOptions = {}): Promise<RecordingInitData> {
            const clientToken = sessionStorage.getItem('clientToken')
            if (!clientToken) {
                throw new Error('Missing client token for recording init')
            }
            const initRes = await fetch('/api/interviews/screening-webrtc/recording/init', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Token': clientToken,
                },
                body: JSON.stringify(options.uploadMbps != null && options.uploadMbps > 0 ? { uploadMbps: options.uploadMbps } : {}),
            })
            const initJson = await initRes.json()
            const data = (initJson?.data || initJson) as RecordingInitData
            const protocol = data.uploadProtocol || (data.blobWriteUrl ? 'azure-blocks' : 'gcs-compose')
            if (!initRes.ok || !data.uploadSessionId || (protocol === 'azure-blocks' && !data.blobWriteUrl)) {
                throw new Error(initJson?.error || 'Failed to initialize recording')
            }
            applyInitData({ ...data, uploadProtocol: protocol })
            return data
        },
        setInitData(data: RecordingInitData) {
            applyInitData(data)
        },
        scheduleFallback(delayMs: number, callback: () => void) {
            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
            }
            fallbackTimer = setTimeout(callback, delayMs)
        },
        cancelFallback() {
            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
                fallbackTimer = null
            }
        },
        setOnRecordingStarted(callback: ((info: RecordingStartedInfo) => void) | null) {
            onRecordingStarted = callback
            if (callback && recordingStartedInfo) {
                callback(recordingStartedInfo)
            }
        },
        setOnRecordingEvent(callback: ((event: RecordingEvent) => void) | null) {
            onRecordingEvent = callback
        },
        getRecordingStartedInfo() {
            return recordingStartedInfo
        },
        getMediaOffsetSeconds() {
            if (!recordingStartedInfo) {
                return null
            }
            return Math.max(0, (performance.now() - recordingStartedInfo.recordingOriginMs) / 1000)
        },
        async requestStart(ctx: InterviewStreamPluginContext) {
            if (started) {
                return
            }
            const hasMedia = ctx.streams.audioTrack || ctx.streams.activeVideoTrack
            if (!initData || !hasMedia) {
                ctx.log(`Recording: not starting (init=${Boolean(initData)} media=${Boolean(hasMedia)})`)
                return
            }

            if (fallbackTimer) {
                clearTimeout(fallbackTimer)
                fallbackTimer = null
            }

            started = true
            const hasRemote = Boolean(ctx.streams.remoteAudioTrack)
            ctx.log(`Recording: starting (mic${hasRemote ? ' + bot' : ''} + camera)`)

            const quality = recordingQualityFrom(initData)
            const resources = await buildRecordingStreamFromState(ctx.streams, connectedTrackIds, {
                width: quality.width,
                height: quality.height,
                fps: quality.fps,
            })
            if (!resources) {
                started = false
                connectedTrackIds.clear()
                return
            }

            audioContext = resources.audioContext
            merger = resources.merger
            localMicGate = resources.localMicGate
            localMicGate.gain.value = localMicMuted ? 0 : 1
            mixTracks = resources.mixTracks
            composedStream = resources.composedStream
            videoCompositor = resources.compositor
            try {
                startRecorder(resources.composedStream, initData.chunkDurationMs || DEFAULT_CHUNK_DURATION_MS, quality, ctx.log)
                hotPlugRemoteAudio(ctx)
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : String(error)
                ctx.log(`Recording: MediaRecorder failed to start: ${message}`)
                // Without this the compositor's draw loop and the AudioContext keep running,
                // and every later requestStart retry stacks another set.
                releaseMediaResources()
                started = false
                mediaRecorder = null
            }
        },
        flushAndStop,
        setLocalMuted(muted: boolean) {
            localMicMuted = muted
            // Short ramp avoids a click in the recording, same idiom as mic ducking.
            if (localMicGate && audioContext && audioContext.state !== 'closed') {
                localMicGate.gain.setTargetAtTime(muted ? 0 : 1, audioContext.currentTime, 0.03)
            }
        },
        async stopAndFinalize() {
            await flushAndStop()

            if (blockMap.size === 0) {
                ctxRef?.log('Recording: skipping finalize (no chunks uploaded)')
                cleanupResources()
                return
            }

            try {
                await finalizeRecording()
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : String(error)
                ctxRef?.log(`Finalize failed: ${message}`)
            }

            cleanupResources()
        },
    }
}
